import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	forwardRef,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
// full calendar has specific condition for import order, react needs to be imported first
/* eslint-disable import/order */
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
/* eslint-enable import/order */
import { formatInTimeZone } from 'date-fns-tz';
import subDays from 'date-fns/subDays';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import { N400, N20, N30, N800, N0, N20A, N50A, N60A, N300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { openViewFromEmbed } from '@atlassian/jira-business-common';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants';
import {
	BusinessDocumentTitle,
	useJqlContext,
	useBusinessEntity,
} from '@atlassian/jira-business-entity-common';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking';
import {
	GqlsRealtime,
	type GqlsRealtimeEvent,
	isIssueEvent,
	type IssueEvent,
	ISSUE_CREATED_EVENT,
	ISSUE_DELETED_EVENT,
} from '@atlassian/jira-business-gqls-realtime';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import { useIssueEvents } from '@atlassian/jira-business-issue-events-observable';
import { ModalIssueApp, SidebarIssueView } from '@atlassian/jira-business-issue-view';
import { CalendarEmbedOnboarding } from '@atlassian/jira-business-onboarding-calendar-embed';
import { PageVisibility } from '@atlassian/jira-business-page-visibility';
import { PerformanceAnalytics, RenderStartMark } from '@atlassian/jira-business-performance';
import { useResizeObserver } from '@atlassian/jira-business-resize-observer';
import { JWMSpaStatePageReady } from '@atlassian/jira-business-spa-state-page-ready';
import { BUSINESS_CALENDAR } from '@atlassian/jira-common-constants/src/analytics-sources';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { errorFlags, FlagRenderer } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useViewMode, VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';
import { useProjectState } from '@atlassian/jira-onboarding-quickstart-core';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import {
	useAnalyticsEvents,
	AnalyticsEventToProps,
	FireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { calendarReleaseForMultipleProjects_businessCalendar$key } from '@atlassian/jira-relay/src/__generated__/calendarReleaseForMultipleProjects_businessCalendar.graphql';
import { ReleaseFlyoutUFOContextProvider } from '@atlassian/jira-release-flyout';
import { useQueryParam } from '@atlassian/jira-router';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration';
import { toIssueKey } from '@atlassian/jira-shared-types';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next';
import {
	AddItemsCalendarNudgeAsync,
	TrelloCrossflowAddItemsCalendarNudgeAsync,
} from '@atlassian/jira-software-onboarding-nudges--next/src/async';
import { useLocale } from '@atlassian/jira-tenant-context-controller';
import UFOLoadHold from '@atlassian/ufo-load-hold';
import { DRAGGING_UNSCHEDULED_ITEM_CLASS_NAME, MAIN_EXPERIENCE } from '../../common/constants';
import { useCalendarReleasesInitializer } from '../../controllers/calendar-releases';
import { useCalendarReleasesForMultipleProjectsInitializer } from '../../controllers/calendar-releases-for-multi-projects';
import { useCalendarIssues } from '../../controllers/calendar-view-items';
import { useDragUnscheduledItem } from '../../controllers/drag-unscheduled-items';
import { useCalendarFeatures } from '../../controllers/features-context';
import { useUnscheduledSidebar } from '../../controllers/unscheduled-items';
import { useUpdateDates } from '../../controllers/update-dates';
import { useCalendarViewUpdaterService } from '../../controllers/view-updater';
import { useWeekStartDay } from '../../controllers/week-start-day';
import type {
	CalendarEvents,
	FullCalendarChangeEvent,
	FullCalendarEvent,
	FullCalendarEventChangeInput,
	FullCalendarEventInput,
	FullCalendarPlaceholderInput,
	FullCalendarRef,
	Release,
} from '../../types';
import { CalendarIssue, EventWrapper } from './calendar-issue';
import CalendarPlaceholder from './calendar-placeholder';
import CalendarRelease from './calendar-release';
import CalendarReleaseForMultipleProjects from './calendar-release-for-multiple-projects';
import { CalendarReleaseMultiProjectOnboardingModal } from './calendar-release-for-multiple-projects-onboarding';
import messages from './messages';
import { Toolbar } from './toolbar';
import type { CalendarViewRangeDateOnly } from './types';
import UnscheduledSidebar from './unscheduled-sidebar/async';
import {
	getInitialCalendarViewRangeDateOnly,
	monthsBetweenCurrentDateOnly,
	getFullCalendarDateProfile,
	calendarEventOrderSort,
} from './utils';

type FullCalendarWithAnalyticsProps = {
	createAnalyticsEvent: () => UIAnalyticsEvent;
};

type OnEventChange = (
	event: FullCalendarEventChangeInput,
	analyticsEvent: UIAnalyticsEvent,
) => void;
type OnDatesSelect = (arg1: FullCalendarChangeEvent, analyticsEvent: UIAnalyticsEvent) => void;

export const FullCalendarWithAnalytics = AnalyticsEventToProps('calendarView', {
	dateClick: 'clicked',
	eventDrop: 'dragged issue',
	eventResize: 'resized issue',
	select: 'dragged selection',
})(
	forwardRef(({ createAnalyticsEvent, ...otherProps }: FullCalendarWithAnalyticsProps, ref) => (
		// @ts-expect-error - TS2769 - No overload matches this call.
		<FullCalendar ref={ref} {...otherProps} />
	)),
);

const FULL_CALENDAR_PLUGINS = [dayGridPlugin, interactionPlugin];

// eslint-disable-next-line complexity
const CalendarView = () => {
	const calendarRef = useRef<FullCalendarRef>(null);
	const calendarWrapperRef = useRef<HTMLElement>(null);
	const isPopoverOpen = useRef<boolean>(false);
	const { formatMessage } = useIntl();
	const locale = useLocale();
	const [hasItemRequirements, setHasItemRequirements] = useState(false);
	const [projectState] = useProjectState();

	const {
		hasInlineCreate,
		hasOnboarding,
		issueViewInteraction,
		featureView,
		hasSoftwareReleases,
		hasScheduleIssues,
	} = useCalendarFeatures();

	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const viewMode = useViewMode();
	const isViewModeSideBar = viewMode === VIEW_MODE_SIDEBAR;

	const pageHeading = formatMessage(messages.calendarHeading);

	const [calendarViewRangeDateOnly, setCalendarViewRangeDateOnly] =
		useState<CalendarViewRangeDateOnly>(getInitialCalendarViewRangeDateOnly());

	const { isUnscheduledSidebarOpen, onClickUnscheduledButton, showIssueFromUnscheduledSidebar } =
		useUnscheduledSidebar({
			UnscheduledSidebar,
			cleanSelectedIssueKey: setSelectedIssueKey,
		});

	const [weekStartDay] = useWeekStartDay();

	const jqlContext = useJqlContext();
	const { data: entity } = useBusinessEntity();

	const request = useMemo(
		() => ({
			jqlContext,
			startDate: calendarViewRangeDateOnly.activeRange.start,
			endDate: calendarViewRangeDateOnly.activeRange.end,
		}),
		[calendarViewRangeDateOnly, jqlContext],
	);

	const {
		issues,
		loading: areIssuesLoading,
		error: issuesError,
		refetch: issuesRefetch,
		setOptimisticItem,
		analyticsAttributes: issuesAnalyticsAttributes,
	} = useCalendarIssues(request);
	const isSharedReleaseDateM1Enabled = expVal(
		'jwm_shared_software_dates_m1_experiment',
		'isMultiProjectsEnabled',
		false,
	);

	const {
		calendarReleasesData: { releases, isLoading: areReleasesLoading },
		CalendarReleasesInitializer,
		analyticsAttributes: releasesAnalyticsAttributes,
	} = isSharedReleaseDateM1Enabled
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCalendarReleasesForMultipleProjectsInitializer({
				releaseDateBefore: calendarViewRangeDateOnly.activeRange.start,
				releaseDateAfter: calendarViewRangeDateOnly.activeRange.end,
			})
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useCalendarReleasesInitializer({
				releaseDateBefore: calendarViewRangeDateOnly.activeRange.start,
				releaseDateAfter: calendarViewRangeDateOnly.activeRange.end,
			});

	const loading = areIssuesLoading || areReleasesLoading;

	const [placeholder, setPlaceholder] = useState<unknown>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useQueryBasedViewAutomaticTracking(MAIN_EXPERIENCE, {
		loading,
		error: issuesError,
	});

	const { loading: startDateFieldLoading } = useStartDateField();

	// only show onboarding spotlight if there are at least 3 items with a dueDate
	useEffect(() => {
		if (!hasOnboarding || hasItemRequirements === true || issues.length < 3) {
			return;
		}
		let count = 0;
		issues.some((issue) => {
			if (issue?.extendedProps?.issue?.duedate != null) {
				count += 1;
			}
			if (count === 3) {
				setHasItemRequirements(true);
			}
			return count === 3;
		});
	}, [issues, hasItemRequirements, hasOnboarding]);

	useEffect(() => {
		const onClickOutsideCalendar = (event: MouseEvent) => {
			// when clicking outside the calendar set the popover as closed
			if (event.target instanceof Node && !calendarWrapperRef?.current?.contains(event.target)) {
				isPopoverOpen.current = false;
			}
		};

		document.addEventListener('mousedown', onClickOutsideCalendar);

		return () => document.removeEventListener('mousedown', onClickOutsideCalendar);
	}, []);

	const updateDates = useUpdateDates();

	const [isSaving, setIsSaving] = useState<boolean>(false);
	const onEventChange: OnEventChange = useCallback(
		async ({ event, revert }, analyticsEvent) => {
			const { issue } = event.extendedProps;
			if (!issue) return;

			const newStartDate = event.startStr;

			let newDueDate = event.startStr;
			if (!isEmpty(event.endStr)) {
				// end date is exclusive in full calendar, hence subtracting one day - https://fullcalendar.io/docs/event-parsing
				const endDateMinusOneDay = subDays(new Date(event.endStr), 1);

				newDueDate = formatInTimeZone(endDateMinusOneDay, 'UTC', 'yyyy-MM-dd');
			}

			setIsSaving(true);
			fireUIAnalytics(analyticsEvent);

			try {
				await updateDates({
					issueKey: issue.key,
					issueId: issue.id,
					newStartDate,
					newDueDate,
				});
				await issuesRefetch();
			} catch (e) {
				revert();
			}
			setIsSaving(false);
		},
		[issuesRefetch, updateDates],
	);

	const isDragging = useRef<boolean>(false);
	const onDragStart = useCallback(() => {
		isDragging.current = true;
	}, []);
	const onDragStop = useCallback(() => {
		isDragging.current = false;
		isPopoverOpen.current = false;
	}, []);

	const isResizing = useRef<boolean>(false);
	const onResizeStart = useCallback(() => {
		isResizing.current = true;
	}, []);
	const onResizeStop = useCallback(() => {
		isResizing.current = false;
	}, []);

	const [isCreating, setIsCreating] = useState(false);
	const onCreatingStart = useCallback(() => {
		setIsCreating(true);
	}, []);
	const onCreatingStop = useCallback(() => {
		setIsCreating(false);
	}, []);

	const isEditing = isDragging.current || isResizing.current || isSaving;

	const { dayCellDidMount } = useDragUnscheduledItem({
		setPlaceholder,
		refetch: issuesRefetch,
		setIsSaving,
		calendarWrapperRef,
	});

	// issue items + placeholder item if applicable
	const allItems = useMemo(
		(): FullCalendarEventInput[] =>
			// @ts-expect-error - TS2322 - Type 'unknown[]' is not assignable to type 'FullCalendarEventInput[]'.
			placeholder ? [...issues, placeholder] : [...issues],
		[issues, placeholder],
	);

	// do not refresh the FullCalendar events while dragging, dropping, or resizing
	// to avoid having items jumping around
	const mappedItems = useRef<FullCalendarEventInput[]>(allItems);

	if (!isEditing) {
		mappedItems.current = allItems;
	}

	const refresh = useCallback(() => {
		if (!isEditing && !isCreating) {
			issuesRefetch();
		}
	}, [isEditing, isCreating, issuesRefetch]);
	let viewUpdaterActions = {
		onIssueCreated: noop,
		onIssueUpdated: noop,
		onIssueRemoved: noop,
	};

	if (fg('jwm_avoid_calendar_blocking_on_realtime_update')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		viewUpdaterActions = useCalendarViewUpdaterService({
			request,
		});
	}

	const { onIssueCreated, onIssueUpdated, onIssueRemoved } = viewUpdaterActions;

	const updateStartEndDate = useCallback(() => {
		if (calendarRef.current) {
			// returned in UTC timezone
			const { currentRange, activeRange } = getFullCalendarDateProfile(
				calendarRef.current.getApi(),
			);

			/**
			 * We'll use fromDateAccordingToUTC because the returned value is in UTC
			 * to convert these dates.
			 */
			const updatedCalendarViewRangeDateOnly = {
				currentRange: {
					start: DateOnly.fromDateAccordingToUTC(currentRange.start),
					end: DateOnly.fromDateAccordingToUTC(currentRange.end),
				},
				activeRange: {
					start: DateOnly.fromDateAccordingToUTC(activeRange.start),
					end: DateOnly.fromDateAccordingToUTC(activeRange.end),
				},
			};

			// if the updated date after user clicking 'Today' button is actually the same as the current date
			// then do nothing
			if (
				updatedCalendarViewRangeDateOnly.currentRange.start.getMonth() ===
				calendarViewRangeDateOnly.currentRange.start.getMonth()
			) {
				return;
			}

			setCalendarViewRangeDateOnly(updatedCalendarViewRangeDateOnly);
		}
	}, [calendarViewRangeDateOnly.currentRange.start]);

	const onNextClick = useCallback(
		(_: React.SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			let monthsFromToday = 0;
			if (calendarRef.current) {
				const calendarApi = calendarRef.current.getApi();

				calendarApi.next();
				updateStartEndDate();

				// returned in UTC timezone
				const { currentRange } = getFullCalendarDateProfile(calendarApi);
				monthsFromToday = monthsBetweenCurrentDateOnly(
					DateOnly.fromDateAccordingToUTC(currentRange.start),
				);
			}

			fireUIAnalytics(analyticsEvent, 'button clicked', 'calendarHeaderNext', {
				monthsFromToday,
			});
		},
		[updateStartEndDate],
	);

	const onPrevClick = useCallback(
		(_: React.SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			let monthsFromToday = 0;
			if (calendarRef.current) {
				const calendarApi = calendarRef.current.getApi();

				calendarApi.prev();
				updateStartEndDate();

				// returned in UTC timezone
				const { currentRange } = getFullCalendarDateProfile(calendarApi);
				monthsFromToday = monthsBetweenCurrentDateOnly(
					DateOnly.fromDateAccordingToUTC(currentRange.start),
				);
			}

			fireUIAnalytics(analyticsEvent, 'button clicked', 'calendarHeaderPrevious', {
				monthsFromToday,
			});
		},
		[updateStartEndDate],
	);

	const onTodayClick = useCallback(
		(_: React.SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			if (calendarRef.current) {
				calendarRef.current.getApi().today();
				updateStartEndDate();
			}

			fireUIAnalytics(analyticsEvent, 'button clicked', 'calendarHeaderToday', {
				monthsFromToday: 0,
			});
		},
		[updateStartEndDate],
	);

	const onDragSelect: OnDatesSelect = useCallback(
		({ startStr, endStr }, analyticsEvent) => {
			if (startDateFieldLoading) {
				return;
			}

			// always remove selection
			if (calendarRef.current) {
				const calendarApi = calendarRef.current.getApi();
				calendarApi.unselect();
			}

			// disable creating new issue when popOver is opened
			// to prevent accidental opening create issue when user is trying to close the popOver
			if (isPopoverOpen.current === true) {
				isPopoverOpen.current = false;
				return;
			}

			setPlaceholder({
				allDay: true,
				start: startStr,
				end: endStr,
			});

			fireUIAnalytics(analyticsEvent, 'dates', {
				startDate: startStr,
				endDate: endStr,
			});
		},
		[startDateFieldLoading],
	);

	const onModalIssueClose = useCallback(() => {
		setSelectedIssueKey(undefined);
	}, [setSelectedIssueKey]);

	const throttledUpdateCalendarSize = useMemo(
		() =>
			throttle(() => {
				if (!calendarRef.current) {
					return;
				}

				calendarRef.current.getApi().updateSize();
			}, 100),
		[calendarRef],
	);

	useResizeObserver(calendarWrapperRef.current, throttledUpdateCalendarSize);

	const onCalendarIssueClick = useCallback(
		(issueKey: string) => {
			// if an issue doesn't have key we should not open issue dialog
			if (!issueKey) {
				return;
			}

			if (issueViewInteraction === 'openNewTab') {
				fireUIAnalytics(createAnalyticsEvent({}), 'issue clicked', 'calendarEmbedIssue');
				openViewFromEmbed({ key: SELECTED_ISSUE_PARAM, value: issueKey });
			} else {
				setSelectedIssueKey(issueKey);
			}
		},
		[createAnalyticsEvent, issueViewInteraction, setSelectedIssueKey],
	);

	const notifyIssueEvent = useIssueEvents(
		fg('jwm_avoid_calendar_blocking_on_realtime_update') ? undefined : refresh,
	);

	const handleRealtimeEvent = useCallback(
		(event: IssueEvent) => {
			const issueId = Number(event.payload.issueId);
			notifyIssueEvent?.(issueId);
			switch (event.type) {
				case ISSUE_DELETED_EVENT:
					onIssueRemoved({ issueId });
					return;
				case ISSUE_CREATED_EVENT:
					onIssueCreated([issueId]);
					return;
				default:
					onIssueUpdated([issueId]);
			}
		},
		[notifyIssueEvent, onIssueCreated, onIssueRemoved, onIssueUpdated],
	);

	const onRealtimeEvent = useCallback(
		(event: GqlsRealtimeEvent) => {
			if (isIssueEvent(event)) {
				if (fg('jwm_avoid_calendar_blocking_on_realtime_update')) {
					handleRealtimeEvent(event);
				} else {
					notifyIssueEvent?.(Number(event.payload.issueId));
				}
			}
		},
		[handleRealtimeEvent, notifyIssueEvent],
	);

	const calendarViews = useMemo(
		() => ({
			dayGridMonth: {
				dayMaxEvents: 3,
				dayCellDidMount,
				// @ts-expect-error - TS7006 - Parameter 'dayCellInfo' implicitly has an 'any' type.
				dayCellContent: (dayCellInfo) => (
					<ConditionalNudgeWrapper
						conditionsToApplyWrapper={dayCellInfo.isToday && !isPopoverOpen.current}
						Wrapper={
							ff('jwm.onboarding.trello-cross-flow') && !projectState?.isSampleProject
								? TrelloCrossflowAddItemsCalendarNudgeAsync
								: AddItemsCalendarNudgeAsync
						}
					>
						<DateWrapper isToday={dayCellInfo.isToday}>{dayCellInfo.date.getDate()}</DateWrapper>
					</ConditionalNudgeWrapper>
				),
				// @ts-expect-error - TS7006 - Parameter 'dayHeaderInfo' implicitly has an 'any' type.
				dayHeaderContent: (dayHeaderInfo) => (
					<DayHeaderWrapper>{dayHeaderInfo.text}</DayHeaderWrapper>
				),
				// @ts-expect-error - TS7006 - Parameter 'moreLinkInfo' implicitly has an 'any' type.
				moreLinkContent: (moreLinkInfo) => (
					<EventWrapper
						backgroundColor="transparent"
						color={token('color.text.subtle', N400)}
						// @ts-expect-error - TS2322 - Type '{ children: any; backgroundColor: string; color: string; hoverColor: string; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<ClassAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement> & { ...; }, any>, any, any>> & Readonly<...> & Readonly<...>'.
						hoverColor={token('color.background.neutral.subtle.hovered', N20)}
					>
						{moreLinkInfo.text}
					</EventWrapper>
				),
				dayPopoverFormat: {
					weekday: 'short',
				},
				moreLinkClick: () => {
					isPopoverOpen.current = true;
					return 'popover';
				},
			},
		}),
		[dayCellDidMount, projectState?.isSampleProject],
	);

	const renderEventContent = useCallback(
		(eventInfo: { event: FullCalendarEvent; isStart: boolean; isMirror: boolean }) => {
			const { event } = eventInfo;
			const issue = event.extendedProps?.issue;
			if (issue) {
				return <CalendarIssue issue={issue} onClick={() => onCalendarIssueClick(issue.key)} />;
			}

			const release = event.extendedProps?.release;

			if (release) {
				if (isSharedReleaseDateM1Enabled) {
					return (
						<CalendarReleaseForMultipleProjects
							releaseRef={
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								release as calendarReleaseForMultipleProjects_businessCalendar$key
							}
						/>
					);
				}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				return <CalendarRelease release={release as Release} />;
			}

			// only show inline create on the first fullcalendar event row
			// and not if this is a mirrored event
			return (
				<CalendarPlaceholder
					withInlineCreate={eventInfo.isStart && !eventInfo.isMirror}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					startStr={(event as FullCalendarPlaceholderInput).startStr}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					endStr={(event as FullCalendarPlaceholderInput).endStr}
					setOptimisticItem={setOptimisticItem}
					onDismiss={() => setPlaceholder(null)}
					request={request}
					onCreatingStart={onCreatingStart}
					onCreatingStop={onCreatingStop}
				/>
			);
		},
		[
			setOptimisticItem,
			request,
			onCreatingStart,
			onCreatingStop,
			onCalendarIssueClick,
			isSharedReleaseDateM1Enabled,
		],
	);

	const onPageVisibilityChange = useCallback(
		({ isVisible }: { isVisible: boolean }) => {
			// refetch the page when the tab or page is visible again
			if (isVisible) {
				refresh();
			}
		},
		[refresh],
	);

	const shouldShowEmbedOnboarding =
		selectedIssueKey === undefined && hasOnboarding && hasItemRequirements;

	const bentoView = useMemo(
		() =>
			selectedIssueKey != null &&
			(!isViewModeSideBar || showIssueFromUnscheduledSidebar) && (
				<ModalIssueApp
					issueKey={toIssueKey(selectedIssueKey)}
					onClose={onModalIssueClose}
					viewModeSwitchDisabled={showIssueFromUnscheduledSidebar}
				/>
			),
		[onModalIssueClose, isViewModeSideBar, selectedIssueKey, showIssueFromUnscheduledSidebar],
	);

	const eventSources = useMemo<CalendarEvents>(
		() => [mappedItems.current, releases],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[releases, mappedItems.current],
	);

	const onEventDataTransform = useCallback((event: FullCalendarEvent) => {
		const issue = event?.extendedProps?.issue;

		if (issue) {
			const isDraggingAllowed = !isOptimisticIssue(issue.id);

			return {
				...event,
				editable: isDraggingAllowed,
			};
		}
		return event;
	}, []);

	const analyticsAttributes = useMemo(
		() => ({
			...releasesAnalyticsAttributes,
			...issuesAnalyticsAttributes,
		}),
		[issuesAnalyticsAttributes, releasesAnalyticsAttributes],
	);

	return (
		<>
			<UFOLoadHold name="calendar-view" hold={loading} />
			<RenderStartMark view={featureView} loading={loading} />
			<BusinessDocumentTitle pageHeading={pageHeading} />

			<Toolbar
				onNextClick={onNextClick}
				onPrevClick={onPrevClick}
				onTodayClick={onTodayClick}
				onUnscheduledClick={onClickUnscheduledButton}
				isUnscheduledSidebarOpen={isUnscheduledSidebarOpen}
				isDisabled={loading}
				currentViewStartDateOnly={calendarViewRangeDateOnly.currentRange.start}
			/>

			<CalendarWrapper data-testid="business-calendar.ui.calendar-view.calendar-wrapper">
				{(loading || (!isViewModeSideBar && selectedIssueKey != null) || isSaving) && (
					// testid used by pollinator tests
					<LoadingWrapper data-testid="business-calendar.ui.calendar-view.loading-wrapper" />
				)}

				{/* @ts-expect-error - TS2322 - Type '{ flag: FlagConfiguration; }' is not assignable to type 'IntrinsicAttributes'. */}
				{issuesError && <FlagRenderer flag={errorFlags.genericError()} />}

				{/* @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'. */}
				<div ref={calendarWrapperRef}>
					<ReleaseFlyoutUFOContextProvider location="calendar" suffixKey="business">
						<FullCalendarWithAnalytics
							ref={calendarRef}
							timeZone="local"
							locale={locale.replace('_', '-')}
							headerToolbar={false}
							plugins={FULL_CALENDAR_PLUGINS}
							initialView="dayGridMonth"
							aspectRatio={1.75}
							// We use a resize observer on the calendar wrapper to handle resize events
							// so we need to disable the internal window resize handling to prevent unnecessary rerenders.
							handleWindowResize={false}
							height="auto"
							// do not allow drag and drop and resizing when a placeholder is displayed
							editable={placeholder == null && hasScheduleIssues}
							eventBorderColor="transparent"
							eventBackgroundColor="transparent"
							eventDataTransform={onEventDataTransform}
							eventDrop={onEventChange}
							eventResize={onEventChange}
							eventDragStart={onDragStart}
							eventDragStop={onDragStop}
							eventResizeStart={onResizeStart}
							eventResizeStop={onResizeStop}
							eventResizableFromStart
							initialDate={calendarViewRangeDateOnly.currentRange.start.toLocalDate()}
							views={calendarViews}
							eventSources={eventSources}
							eventOrder={calendarEventOrderSort}
							eventContent={renderEventContent}
							firstDay={weekStartDay}
							selectable
							select={hasInlineCreate ? onDragSelect : undefined}
						/>
					</ReleaseFlyoutUFOContextProvider>
				</div>

				{!loading && (
					<FireTrackAnalytics eventName="calendar viewed" attributes={analyticsAttributes} />
				)}
			</CalendarWrapper>

			{isViewModeSideBar && (
				<SidebarIssueView
					issueKey={showIssueFromUnscheduledSidebar ? undefined : selectedIssueKey}
					onClose={onModalIssueClose}
					analyticsSource={BUSINESS_CALENDAR}
				/>
			)}
			{bentoView}
			{shouldShowEmbedOnboarding && <CalendarEmbedOnboarding />}

			{hasSoftwareReleases && isSharedReleaseDateM1Enabled && (
				<CalendarReleaseMultiProjectOnboardingModal />
			)}
			{hasSoftwareReleases && <CalendarReleasesInitializer />}

			<GqlsRealtime appId="business-calendar" entityId={entity?.id} onReceive={onRealtimeEvent} />
			<PageVisibility onChange={onPageVisibilityChange} />
			<PerformanceAnalytics view={featureView} loading={loading} />
			{!loading && <JWMSpaStatePageReady />}
		</>
	);
};

export default CalendarView;

export const DayHeaderWrapper = ({ children }: { children: ReactNode }) => (
	<DayHeaderTitle>
		<Heading level="h200" as="div">
			{children}
		</Heading>
	</DayHeaderTitle>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarWrapper = styled.div({
	'--fc-border-color': token('color.border.disabled', N30),
	color: token('color.text', N800),
	backgroundColor: token('elevation.surface', N0),
	borderRadius: '8px',
	flex: 'auto',
	overflow: 'auto',
	padding: `0 ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&': {
		position: 'relative',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		tbody: {
			backgroundColor: token('elevation.surface', N0),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:last-of-type': {
				borderRight: 0,
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'thead, tbody, tfoot': {
			border: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-scroller-harness': {
			backgroundColor: token('elevation.surface', N0),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-daygrid-day-top': {
			justifyContent: 'center',
			position: 'relative',
			zIndex: 1,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc .fc-daygrid-day.fc-day-today': {
			background: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-theme-standard .fc-scrollgrid': {
			border: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-theme-standard td, .fc-theme-standard th': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:last-of-type': {
				borderRight: 0,
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.fc-theme-standard tr:last-of-type > td': {
			borderBottom: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-daygrid-day-number': {
			color: token('color.text.subtle', N400),
			'&:hover': {
				textDecoration: 'none',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-daygrid-dot-event:hover': {
			background: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-daygrid-day': {
			minHeight: '140px',
			height: '13.5vh',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-daygrid-more-link': {
			textDecoration: 'none',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			button: {
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				display: 'block',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc-col-header-cell-cushion': {
			textDecoration: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc .fc-highlight': {
			backgroundColor: token('color.blanket.selected', N20A),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`.${DRAGGING_UNSCHEDULED_ITEM_CLASS_NAME} .fc-event`]: {
			pointerEvents: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.fc-event-dragging:not(.fc-event-selected)': {
			cursor: 'grabbing',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.fc-event-main': {
				backgroundColor: token('elevation.surface.overlay', N0),
				boxShadow: token('elevation.shadow.overlay', `0 4px 8px -2px ${N50A}, 0 0 1px ${N60A}`),
				borderRadius: '4px',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc .fc-day-other .fc-daygrid-day-top': {
			opacity: 0.6,
			color: token('color.text.subtlest', N300),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fc .fc-popover.fc-more-popover': {
			width: '240px',
			border: 'none',
			borderRadius: '3px',
			boxShadow:
				// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
				'var(--overlay, 0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31))',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.fc-popover-header': {
				backgroundColor: token('elevation.surface.raised', '#fff'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'.fc-popover-title': {
					margin: 'auto',
					textTransform: 'uppercase',
					// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
					fontSize: '12px',
					fontWeight: 600,
					letterSpacing: 0,
					lineHeight: '16px',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'.fc-popover-close': {
					display: 'none',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.fc-popover-body': {
				padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
				backgroundColor: token('elevation.surface.raised', '#fff'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'.fc-more-popover-misc': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
					'> div': {
						// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
						fontSize: '16px',
						fontWeight: 600,
						letterSpacing: '-0.006em',
						lineHeight: '20px',
						margin: `0 auto ${token('space.050', '4px')}`,
						cursor: 'unset',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					},
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'.fc-daygrid-event-harness': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
					'.fc-daygrid-event.fc-event-start, .fc-daygrid-event.fc-event-end': {
						marginRight: 0,
						marginLeft: 0,
					},
				},
			},
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateWrapper = styled.div<{ isToday: boolean }>({
	fontWeight: 600,
	lineHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	textAlign: 'center',
	width: '24px',
	height: '24px',
	borderRadius: '30px',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isToday }) =>
		isToday ? token('color.background.neutral.bold', N800) : 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isToday }) =>
		isToday ? token('color.text.inverse', '#fff') : token('color.text.subtle', N400),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isToday }) =>
			isToday
				? token('color.background.neutral.bold', N800)
				: token('color.background.neutral.subtle.hovered', N30),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DayHeaderTitle = styled.div({
	textTransform: 'uppercase',
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingWrapper = styled.div({
	position: 'absolute',
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	backgroundColor: token('color.skeleton', 'rgba(255, 255, 255, 0.3)'),
	zIndex: 5,
});
